/*
 * This model holds the data for curriculum api access page of the calcualtor
 *
 */
const extend = require('extend')
import { eachSeries, eachLimit } from 'common/helpers/async-tools'
import httpClient from 'client/js/models/http-client'

// In-memory cached response data
let cacheData = { }
const DEBUG = false

// Track active course updates
let courseCacheUpdates = { }

/**
 * Get conditional set item type based on the query parameters
 */
function getConditionSetItemType(query) {
   const acceptableQueries = ['subject', 'major', 'co_major', 'minor']

   let q = query.substring(query.indexOf('=') + 1, query.indexOf('?'))

   if (!acceptableQueries.includes(q)) {
      q = query.substring(0, query.indexOf('&'))
   }

   return q;
}

/**
 * Transform condition set response into a standardised items
 */
function transformConditionSetResponse(q, response) {
   const resp = { items: [], links: response.data.links };

   let fieldPrefix = q;
   let itemType = '';
   switch (q) {
      case 'subject':
         fieldPrefix = 'unit';
         itemType = 'Unit';
         break;
      case 'major':
         itemType = 'Major';
         break;
      case 'co_major':
         itemType = 'Co-Major';
         break;
      case 'minor':
         itemType = 'Minor';
         break;
      default:
         fieldPrefix = '';
         itemType = '';
         break;
   }


   if (fieldPrefix && itemType) {
      const activeItems = response.data.conditionSet[q].filter(
         (item) => item.status.value === 'Active' && item.callista_version_number
      );

      resp.items = activeItems.map((item) => {
         const mappedItem = {
            name: item[`${fieldPrefix}_title`],
            credits: parseFloat(item.credit_points),
            code: item[`${fieldPrefix}_code`],
            id: item[`${fieldPrefix}_code`] + 'v' + item.callista_version_number,
            order: '0',
            description: '',
            sourceSystemId: item.cl_id,
            type: itemType,
            version: item.version_name,
         }

         if (item.detailed_field_of_education && item.detailed_field_of_education.value) {
            mappedItem.fieldOfEducation = {
               detailed: item.detailed_field_of_education.value
            };
         }

         return mappedItem;
      });
   }

   return resp;
}

function mapConditionSetData(data) {
   // It's much easier to map the Cached Condition Set data, since most of the work has been done by ICC.
   return data.map((item,i) => ({
      code: item.code,
      id: `${item.code}v${item.callistaVersion}`,
      credits: item.creditPoints,
      name: item.title,
      order: i,
      sourceSystemId: item.clId,
      type: item.type == 'Subject' ? 'Unit' : item.type,
      status: item.type == 'Subject' && item.status == 'Approved' ? 'Active' : item.status,
      masterStatus: item.masterStatus,
      courseLoopVersionNumber: item.version,
      version: item.version,
      // TODO: Is description ever not blank? (Not currently in Cached Condition Set response.)
      description: '',
      raw: item
   }))
}

async function cachedQuery(id, query) {
   let data = cacheData[id]
   // Simple cache on success
   if (!(id in cacheData)) {
      try {
         cacheData[id] = data = await query()
         if (DEBUG) console.log('Fetched & Stored:',id)
      } catch (err) {
         if (DEBUG) console.log('Error:',id)
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw(err)
      }
   } else {
      if (DEBUG) console.log('Cached:',id)
   }

   // Deep-clone the cached data object (so callers don't fiddle with it)
   return extend(true, {}, data)
}

// Start periodic polling of course cache updates
setInterval(async () => {
   let updates = Object.keys(courseCacheUpdates)
   if (updates.length > 0) {
      await eachLimit(updates, async (courseCode) => {
         let status = await Curriculum.getCourseCacheStatus(courseCode, courseCacheUpdates[courseCode].version)
         // console.log(`Course ${courseCode} cache status:`, status)
         if (status.cacheTimestamp != courseCacheUpdates[courseCode].ts) {
            // console.log(`Course ${courseCode} cache updated`)
            delete courseCacheUpdates[courseCode]
         }
      })
   }
}, 10000) // Every 10 seconds

class Curriculum {
   static async searchCourses(keywords, type, activeOnly = true) {
      try {
         let response = await httpClient.post(BaseURL + "/api/curriculum/course-search/", { id: keywords, type, onlyActive: activeOnly })
         return response.data
      } catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   static async courseList() {
      return await cachedQuery('course-list', async () => {
         let response = await httpClient.post(BaseURL + "/api/curriculum/course-list/")
         return response.data
      })
   }

   // Not used?
   static async searchCoursesPage(transaction, page) {
      try {
         let response = await httpClient.post(BaseURL + "/api/curriculum/course-search/", { transaction, page })
         return response.data
      } catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   static async searchUnits(id) {
      try {
         let response = await httpClient.post(BaseURL + "/api/curriculum/unit-search/", { id })
         return response.data
      } catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   // Requires "version" if you want to get a Draft version
   static async getUnit(id, version) {
      return await cachedQuery(`get-unit/${id}`, async () => {
         let response = await httpClient.post(BaseURL + "/api/curriculum/unit/", { id, version })
         return response.data
      })
   }

   // Get course details - uncached to allow for server-side cache forced updates
   static async getCourse(id, version, inc_draft) {
      let response = await httpClient.post(BaseURL + "/api/curriculum/course/", { id, version, inc_draft })
      let course = response.data

      // Check for missing relationship unit details in the structure (this is a backup - details SHOULD be provided by ICC but there's a current issue)
      if (course.structure.length)
         await Curriculum.check_relationship_details(id, course.structure[0])

      return course
   }

   static async getMajorDetail(id) {
      return await cachedQuery(`get-major-detail/${id}`, async () => {
         let response = await httpClient.post(BaseURL + "/api/curriculum/major/", { id })
         return response.data
      })
   }

   static async getMajor(id, version, inc_draft) {
      return await cachedQuery(`get-major/${id}:${version}`, async () => {
         let response = await httpClient.post(BaseURL + "/api/curriculum/majors-units/", { id, version, inc_draft })
         return response.data
      })
   }

   static async getCoMajor(id, version, inc_draft) {
      return await cachedQuery(`get-comajor/${id}:${version}`, async () => {
         let response = await httpClient.post(BaseURL + "/api/curriculum/co-major-units/", { id, version, inc_draft })
         return response.data
      })
   }

   static async getMinor(id, version, inc_draft) {
      return await cachedQuery(`get-minor/${id}:${version}`, async () => {
         let response = await httpClient.post(BaseURL + "/api/curriculum/minor-units/", { id, version, inc_draft })
         return response.data
      })
   }

   // Not used?
   static async getCoursePage(transaction, page) {
      try {
         let response = await httpClient.post(BaseURL + "/api/curriculum/course-page/", { transaction, page })
         return response.data
      } catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   // Not used?
   static async getUnitPage(transaction, page) {
      try {
         let response = await httpClient.post(BaseURL + "/api/curriculum/unit-page/", { transaction, page })
         return response.data
      } catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   static async getConditionSet(query) {
      try {
         const q = getConditionSetItemType(query);
         if (!q) {
            return { items: [] };
         }

         let response = await httpClient.post(BaseURL + '/api/curriculum/get-condition-set/', { query })
         return transformConditionSetResponse(q, response);

      } catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   static async getCachedConditionSet(id, inc_draft, skip_version_update) {
      console.log('getCachedConditionSet', id, inc_draft, skip_version_update, `inc_draft=${inc_draft||''}${skip_version_update?'&skip_update='+skip_version_update:''}`)
      try {
         let response = await httpClient.get(BaseURL + `/api/curriculum/get-condition-set/${id}?inc_draft=${inc_draft||''}${skip_version_update?'&skip_update='+skip_version_update:''}`)

         let items = mapConditionSetData(response.data)

         return { items, links: {} }
      } catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   static async searchCachedConditionSet(id, match, inc_draft) {
      try {
         let response = await httpClient.get(BaseURL + `/api/curriculum/search-cached-condition-set/${id}?match=${match}&inc_draft=${inc_draft}`)
         let items = mapConditionSetData(response.data)
         return { items }
      } catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   static async getConditionSetPage(type, transaction, page) {
      try {
         const query = `${type}&cl_transaction=${transaction}&cl_page=${page}`;
         let response = await httpClient.post(BaseURL + '/api/curriculum/get-condition-set/', { query })
         return transformConditionSetResponse(type, response);
      } catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   static async searchConditionSet(query) {
      try {
         const q = getConditionSetItemType(query);
         if (!q) {
            return { items: [] };
         }

         const response = await httpClient.post(BaseURL + '/api/curriculum/search-condition-set/', { query });
         return transformConditionSetResponse(q, response);
      }
      catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }


   static getRelationshipHandbookUrl(relationship) {
      const baseUrl = BaseURL + '/api/curriculum/handbook/';
      if (relationship.type == "Unit") {
         if (relationship.unit) {
            return baseUrl + "units/" + relationship.unit.courseLoopVersionNumber + "/" + relationship.unit.code;
         } else {
            return baseUrl + "units/" + relationship.version + "/" + relationship.code;
         }
      }

      return ''
   }

   static getCourseHandbookUrl(course) {
      const baseUrl = BaseURL + '/api/curriculum/handbook/';
      if (course) {
         return baseUrl + `course/${course.courseLoopVersionNumber}/${course.code}`
      }
      return ''
   }

   static async refreshCourseCache(course_id, version) {
      try {
         let versionQuery = version ? `?version=${version}` : ''
         const response = await httpClient.post(BaseURL + `/api/curriculum/update-course/${course_id}${versionQuery}`)
         courseCacheUpdates[course_id] = { ts: response.data.cacheTimestamp, version }
         return response.data
      }
      catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   // Return true if the course is still being updated
   static getCourseCacheRefreshStatus(course_id, version) {
      return !!courseCacheUpdates[course_id] && courseCacheUpdates[course_id].version === version
   }

   static async getCourseCacheStatus(course_id, version) {
      try {
         let versionQuery = version ? `?version=${version}` : ''
         const response = await httpClient.get(BaseURL + `/api/curriculum/update-course/${course_id}${versionQuery}`)
         return response.data
      }
      catch (err) {
         let response = err.response
         if (response && response.data.response)
            throw new Error(response.data.response)
         throw (err)
      };
   }

   /**
    * Walk through Course structure and check Unit Relationships that are missing Unit details
    * This is a work-around/backup - details SHOULD be provided by ICC but there's a current issue
    * This is done by the client to avoid timeout issues doing it all in one server API call
    * @param {*} course_id
    * @param {*} container
    * @param {*} depth
    */
   static async check_relationship_details(course_id, container, depth=0) {
      console.log(`${depth}: Course ${course_id}, Relationships ${container.relationship?.length}, Container ${container.container?.length}, Containers ${container.containers?.length}`)
      if (container.relationship?.length) {
         await eachLimit(container.relationship, async (reln) => {
            if (reln.type == 'Unit' && !reln.unit) {
               // Fetch missing Unit data
               console.warn(`Course ${course_id}: Fetching missing unit data ${reln.id}`)
               try {
                  reln.unit = await Curriculum.getUnit(reln.code, reln.version)
               } catch (e) {
                  // FFS!
                  console.error(`Course ${course_id}: Failed to fetch missing unit data ${reln.id} - ${e.code} ${e.message}`)
                  // Bare-bones fake data to at least not break the UI
                  reln.unit = { offerings: [], code: reln.code, name: reln.name, credits: reln.credits, status: "Error" }
               }
            }
         })
      }

      if (container.container?.length)
         await eachSeries(container.container, async (subcontainer) => await Curriculum.check_relationship_details(course_id, subcontainer, depth+1))
      if (container.containers?.length)
         await eachSeries(container.containers, async (subcontainer) => await Curriculum.check_relationship_details(course_id, subcontainer, depth+1))
   }
}

module.exports = Curriculum
